import { emoteMessageSchema, NETWORK_PROTOCOL_VERSION, opponentSeat, type NetworkEmote, type NetworkRoomConfig, type NetworkSeat, type RoomSession, type ServerControlMessage } from './contracts';
import { NETWORK_LATENCY_PROBE_SAMPLES, NETWORK_LATENCY_PROBE_TIMEOUT_MS, NETWORK_MAX_INPUT_DELAY_TICKS, NETWORK_MIN_INPUT_DELAY_TICKS, NETWORK_RECONNECT_DEADLINE_MS, networkInputDelayTicks } from './timing';

export interface NetworkLinkStats {
  readonly roundTripMs: number | null;
  readonly inputDelayTicks: number;
}

export interface NetworkTransportTraceEvent {
  readonly atMs: number;
  readonly kind: string;
  readonly state?: string;
  readonly attempt?: number;
}

export interface NetworkTransportDebugSnapshot {
  readonly schemaVersion: 1;
  readonly linkStats: NetworkLinkStats;
  readonly route: 'direct' | 'relay' | 'unknown';
  readonly candidateTransport: 'udp' | 'tcp' | 'unknown';
  readonly messagesSent: number;
  readonly messagesReceived: number;
  readonly bufferedAmountHighWater: number;
  readonly reconnectAttempts: number;
  readonly trace: readonly NetworkTransportTraceEvent[];
}

export interface PeerChannel {
  readonly config: NetworkRoomConfig;
  readonly linkStats: NetworkLinkStats;
  send(message: string): void;
  sendEmote(emote: NetworkEmote): void;
  onMessage(listener: (message: string) => void): () => void;
  onEmote(listener: (emote: NetworkEmote) => void): () => void;
  onDisconnect(listener: () => void): () => void;
  onReconnect(listener: () => void): () => void;
  onConnectionLost(listener: () => void): () => void;
  debugSnapshot?(): NetworkTransportDebugSnapshot;
  close(): void;
}

type SignalPayload =
  | Readonly<{ description: RTCSessionDescriptionInit }>
  | Readonly<{ candidate: RTCIceCandidateInit }>;

/** WebSocket is signaling/control only; battle inputs use one ordered reliable data channel. */
export class WebRtcPeer {
  private socket: WebSocket | null = null;
  private connection: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private emoteChannel: RTCDataChannel | null = null;
  private config: NetworkRoomConfig | null = null;
  private makingOffer = false;
  private ignoreOffer = false;
  private settingRemoteAnswer = false;
  private readonly messageListeners = new Set<(message: string) => void>();
  private readonly pendingMessages: string[] = [];
  private readonly disconnectListeners = new Set<() => void>();
  private readonly reconnectListeners = new Set<() => void>();
  private readonly connectionLostListeners = new Set<() => void>();
  private readonly emoteListeners = new Set<(emote: NetworkEmote) => void>();
  private readonly pendingSignals: string[] = [];
  private readonly pendingDataMessages: string[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private resolved = false;
  private intentionallyClosed = false;
  private emoteSequence = 1;
  private pendingEmote: NetworkEmote | null = null;
  private lastRemoteEmoteSequence = 0;
  private lastRemoteEmoteAt = 0;
  private reconnectAttempts = 0;
  private disconnected = false;
  private linkStats: NetworkLinkStats | null = null;
  private latencyProbeSequence = 0;
  private readonly latencyProbeSentAt = new Map<number, number>();
  private readonly latencySamples: number[] = [];
  private readyResolve: ((channel: PeerChannel) => void) | null = null;
  private readyReject: ((error: Error) => void) | null = null;
  private connectionTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDeadlineTimer: ReturnType<typeof setTimeout> | null = null;
  private latencyProbeTimer: ReturnType<typeof setTimeout> | null = null;
  private onPeerFound: (() => void) | null = null;
  private readonly transportTrace: NetworkTransportTraceEvent[] = [];
  private messagesSent = 0;
  private messagesReceived = 0;
  private bufferedAmountHighWater = 0;
  private selectedRoute: NetworkTransportDebugSnapshot['route'] = 'unknown';
  private candidateTransport: NetworkTransportDebugSnapshot['candidateTransport'] = 'unknown';

  constructor(
    private readonly session: RoomSession,
    private readonly iceServers: readonly RTCIceServer[],
    private readonly connectionTimeoutMs = 30_000,
    private readonly reconnectDeadlineMs = NETWORK_RECONNECT_DEADLINE_MS,
  ) {}

  connect(signal?: AbortSignal, onPeerFound?: () => void): Promise<PeerChannel> {
    if (this.socket || this.connection) throw new Error('Peer connection already started');
    this.onPeerFound = onPeerFound ?? null;
    const ready = new Promise<PeerChannel>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    const connection = new RTCPeerConnection({ iceServers: [...this.iceServers] });
    this.connection = connection;
    this.traceTransport('peer-connection-created');
    connection.onicecandidate = ({ candidate }) => {
      if (candidate) this.sendSignal({ candidate: candidate.toJSON() });
    };
    connection.onnegotiationneeded = () => {
      void this.negotiate().catch(() => {
        this.notifyDisconnected();
        this.scheduleReconnect();
      });
    };
    connection.onconnectionstatechange = () => {
      this.traceTransport('connection-state', connection.connectionState);
      if (connection.connectionState === 'failed') void connection.restartIce();
      if (connection.connectionState === 'connected' && this.channel?.readyState === 'open') this.notifyConnected();
      if (connection.connectionState === 'failed' || connection.connectionState === 'closed' || connection.connectionState === 'disconnected') {
        this.notifyDisconnected();
        this.scheduleReconnect();
      }
    };
    connection.oniceconnectionstatechange = () => this.traceTransport('ice-connection-state', connection.iceConnectionState);
    connection.onicegatheringstatechange = () => this.traceTransport('ice-gathering-state', connection.iceGatheringState);
    connection.onsignalingstatechange = () => this.traceTransport('signaling-state', connection.signalingState);
    connection.ondatachannel = ({ channel }) => this.attachChannel(channel);

    this.openSocket();
    if (signal) {
      const abort = () => { this.close(); this.rejectReady(new DOMException('Connection cancelled', 'AbortError')); };
      if (signal.aborted) abort(); else signal.addEventListener('abort', abort, { once: true });
    }
    return ready;
  }

  private attachChannel(channel: RTCDataChannel): void {
    if (channel.label === 'towerarena-emotes') { this.attachEmoteChannel(channel); return; }
    if (channel.label !== 'towerarena-inputs') { channel.close(); return; }
    if (this.channel && this.channel !== channel) this.channel.close();
    this.channel = channel;
    channel.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      this.messagesReceived += 1;
      if (this.handlePeerControl(event.data)) return;
      if (this.messageListeners.size === 0) this.pendingMessages.push(event.data);
      else for (const listener of this.messageListeners) listener(event.data);
    };
    channel.onclose = () => {
      this.traceTransport('input-channel', 'closed');
      if (this.channel === channel) this.channel = null;
      this.notifyDisconnected();
      this.ensureHostChannels();
      this.scheduleReconnect();
    };
    channel.onerror = () => { this.traceTransport('input-channel', 'error'); this.notifyDisconnected(); this.scheduleReconnect(); };
    channel.onopen = () => {
      this.traceTransport('input-channel', 'open');
      for (const message of this.pendingDataMessages.splice(0)) channel.send(message);
      this.reconnectAttempts = 0;
      if (this.reconnectTimer !== null) { globalThis.clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
      this.sendControl({ type: 'peer-ready' });
      if (this.resolved) this.notifyConnected();
      else this.startLatencyProbe();
      this.resolveIfReady();
      void this.captureSelectedRoute();
    };
  }

  private attachEmoteChannel(channel: RTCDataChannel): void {
    if (this.emoteChannel && this.emoteChannel !== channel) this.emoteChannel.close();
    this.emoteChannel = channel;
    channel.onclose = () => {
      if (this.emoteChannel === channel) this.emoteChannel = null;
      this.ensureHostChannels();
    };
    channel.onopen = () => {
      if (this.pendingEmote === null) return;
      const emote = this.pendingEmote;
      this.pendingEmote = null;
      this.sendEmote(emote);
    };
    channel.onmessage = (event) => {
      if (typeof event.data !== 'string' || event.data.length > 1_024) return;
      let value: unknown;
      try { value = JSON.parse(event.data); } catch { return; }
      const parsed = emoteMessageSchema.safeParse(value);
      if (!parsed.success || parsed.data.networkMatchId !== this.session.networkMatchId || parsed.data.seat !== opponentSeat(this.session.seat) || parsed.data.sequence <= this.lastRemoteEmoteSequence || Date.now() - this.lastRemoteEmoteAt < 750) return;
      this.lastRemoteEmoteSequence = parsed.data.sequence;
      this.lastRemoteEmoteAt = Date.now();
      for (const listener of this.emoteListeners) listener(parsed.data.emote);
    };
  }

  private async negotiate(): Promise<void> {
    const connection = this.connection;
    if (!connection) return;
    try {
      this.makingOffer = true;
      await connection.setLocalDescription();
      if (connection.localDescription) this.sendSignal({ description: connection.localDescription.toJSON() });
    } finally {
      this.makingOffer = false;
    }
  }

  private ensureHostChannels(): void {
    const connection = this.connection;
    if (this.intentionallyClosed || this.session.seat !== 'seat-a' || !this.config || !connection || connection.connectionState === 'closed') return;
    if (!this.channel || this.channel.readyState === 'closed') this.attachChannel(connection.createDataChannel('towerarena-inputs', { ordered: true }));
    if (!this.emoteChannel || this.emoteChannel.readyState === 'closed') this.attachEmoteChannel(connection.createDataChannel('towerarena-emotes', { ordered: false, maxRetransmits: 0 }));
  }

  private startLatencyProbe(): void {
    if (this.intentionallyClosed || this.resolved || this.linkStats || this.session.seat !== 'seat-a' || !this.config || this.channel?.readyState !== 'open' || this.latencyProbeTimer !== null) return;
    this.latencyProbeTimer = globalThis.setTimeout(() => this.finishLatencyProbe(), NETWORK_LATENCY_PROBE_TIMEOUT_MS);
    this.sendLatencyProbe();
  }

  private sendLatencyProbe(): void {
    const channel = this.channel;
    if (!channel || channel.readyState !== 'open' || this.latencyProbeSequence >= NETWORK_LATENCY_PROBE_SAMPLES) return;
    const sequence = ++this.latencyProbeSequence;
    this.latencyProbeSentAt.set(sequence, performance.now());
    channel.send(JSON.stringify({ type: 'peer-latency-probe', protocolVersion: NETWORK_PROTOCOL_VERSION, sequence }));
    this.messagesSent += 1;
  }

  private finishLatencyProbe(): void {
    if (this.linkStats || this.session.seat !== 'seat-a') return;
    if (this.latencyProbeTimer !== null) globalThis.clearTimeout(this.latencyProbeTimer);
    this.latencyProbeTimer = null;
    const roundTripMs = this.latencySamples.length > 0 ? Math.ceil(Math.max(...this.latencySamples)) : null;
    const inputDelayTicks = networkInputDelayTicks(roundTripMs, this.config?.rules.tickRate ?? 30);
    this.linkStats = { roundTripMs, inputDelayTicks };
    if (this.channel?.readyState === 'open') this.channel.send(JSON.stringify({ type: 'peer-latency-config', protocolVersion: NETWORK_PROTOCOL_VERSION, roundTripMs, inputDelayTicks }));
    this.resolveIfReady();
  }

  private handlePeerControl(raw: string): boolean {
    if (raw.length > 1_024) return false;
    let value: unknown;
    try { value = JSON.parse(raw); } catch { return false; }
    if (!value || typeof value !== 'object') return false;
    const message = value as Record<string, unknown>;
    if (message.type !== 'peer-latency-probe' && message.type !== 'peer-latency-probe-response' && message.type !== 'peer-latency-config') return false;
    if (message.protocolVersion !== NETWORK_PROTOCOL_VERSION) return true;
    if (message.type === 'peer-latency-probe') {
      if (this.session.seat === 'seat-b' && Number.isInteger(message.sequence) && Number(message.sequence) > 0 && Number(message.sequence) <= NETWORK_LATENCY_PROBE_SAMPLES && this.channel?.readyState === 'open') {
        this.channel.send(JSON.stringify({ type: 'peer-latency-probe-response', protocolVersion: NETWORK_PROTOCOL_VERSION, sequence: Number(message.sequence) }));
        this.messagesSent += 1;
      }
      return true;
    }
    if (message.type === 'peer-latency-probe-response') {
      if (this.session.seat !== 'seat-a' || this.linkStats || !Number.isInteger(message.sequence)) return true;
      const sentAt = this.latencyProbeSentAt.get(Number(message.sequence));
      if (sentAt === undefined) return true;
      this.latencyProbeSentAt.delete(Number(message.sequence));
      this.latencySamples.push(Math.max(0, performance.now() - sentAt));
      if (this.latencySamples.length >= NETWORK_LATENCY_PROBE_SAMPLES) this.finishLatencyProbe();
      else this.sendLatencyProbe();
      return true;
    }
    const roundTripMs = message.roundTripMs === null ? null : Number(message.roundTripMs);
    const inputDelayTicks = Number(message.inputDelayTicks);
    if (this.session.seat === 'seat-b' && (roundTripMs === null || Number.isFinite(roundTripMs) && roundTripMs >= 0 && roundTripMs <= 10_000) && Number.isInteger(inputDelayTicks) && inputDelayTicks >= NETWORK_MIN_INPUT_DELAY_TICKS && inputDelayTicks <= NETWORK_MAX_INPUT_DELAY_TICKS) {
      this.linkStats = { roundTripMs, inputDelayTicks };
      this.resolveIfReady();
    }
    return true;
  }

  private async handleControl(raw: string): Promise<void> {
    let message: ServerControlMessage;
    try { message = JSON.parse(raw) as ServerControlMessage; }
    catch { return; }
    if (message.type === 'error') { this.rejectReady(new Error(message.code)); return; }
    const connection = this.connection;
    if (!connection) return;
    if (message.type === 'room-state') {
      this.config = message.config;
      if (message.status === 'ready' && message.config) this.peerFound();
      this.startLatencyProbe();
      this.resolveIfReady();
      return;
    }
    if (message.type === 'match-ready') {
      if (message.networkMatchId !== this.session.networkMatchId) return;
      this.peerFound();
      this.ensureHostChannels();
      return;
    }
    if (message.type !== 'signal') return;
    const payload = message.data as SignalPayload;
    if ('description' in payload) {
      const description = payload.description;
      const readyForOffer = !this.makingOffer && (connection.signalingState === 'stable' || this.settingRemoteAnswer);
      const offerCollision = description.type === 'offer' && !readyForOffer;
      this.ignoreOffer = this.session.seat === 'seat-a' && offerCollision;
      if (this.ignoreOffer) return;
      this.settingRemoteAnswer = description.type === 'answer';
      await connection.setRemoteDescription(description);
      this.settingRemoteAnswer = false;
      if (description.type === 'offer') {
        await connection.setLocalDescription();
        if (connection.localDescription) this.sendSignal({ description: connection.localDescription.toJSON() });
      }
      return;
    }
    if ('candidate' in payload) {
      try { await connection.addIceCandidate(payload.candidate); }
      catch (error) { if (!this.ignoreOffer) throw error; }
    }
  }

  private sendSignal(data: SignalPayload): void {
    const message = JSON.stringify({ type: 'signal', data });
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(message);
    else this.pendingSignals.push(message);
  }

  private sendControl(value: unknown): void {
    const message = JSON.stringify(value);
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(message);
    else this.pendingSignals.push(message);
  }

  private openSocket(): void {
    if (this.intentionallyClosed) return;
    const socket = new WebSocket(this.session.websocketUrl);
    this.socket = socket;
    this.traceTransport('signaling-socket', 'connecting');
    socket.onopen = () => {
      this.traceTransport('signaling-socket', 'open');
      for (const message of this.pendingSignals.splice(0)) socket.send(message);
      if (this.resolved) this.sendControl({ type: 'resume' });
    };
    socket.onmessage = (event) => { void this.handleControl(String(event.data)).catch((error: unknown) => this.rejectReady(error instanceof Error ? error : new Error(String(error)))); };
    socket.onerror = () => { this.traceTransport('signaling-socket', 'error'); if (!this.resolved) this.rejectReady(new Error('Signaling connection failed')); };
    socket.onclose = (event) => {
      this.traceTransport('signaling-socket', `closed:${event?.code ?? 0}`);
      if (!this.resolved && this.channel?.readyState !== 'open') this.rejectReady(new Error('Signaling connection closed'));
      if (this.resolved) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.intentionallyClosed || this.reconnectTimer !== null || this.reconnectAttempts >= 30) return;
    this.reconnectTimer = globalThis.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.intentionallyClosed) return;
      this.reconnectAttempts += 1;
      this.traceTransport('reconnect-attempt', undefined, this.reconnectAttempts);
      if (!this.socket || this.socket.readyState === WebSocket.CLOSED) this.openSocket();
      else if (this.socket.readyState === WebSocket.OPEN) this.sendControl({ type: 'resume' });
      if (this.connection?.connectionState === 'failed' || this.connection?.connectionState === 'disconnected') this.connection.restartIce();
      this.ensureHostChannels();
      if (this.channel?.readyState !== 'open') this.scheduleReconnect();
    }, 1_000);
  }

  private resolveIfReady(): void {
    if (!this.readyResolve || !this.config || !this.linkStats || this.channel?.readyState !== 'open') return;
    this.clearConnectionTimeout();
    const api: PeerChannel = {
      config: this.config,
      linkStats: this.linkStats,
      send: (message) => {
        if (this.channel?.readyState === 'open') {
          this.channel.send(message);
          this.messagesSent += 1;
          this.bufferedAmountHighWater = Math.max(this.bufferedAmountHighWater, this.channel.bufferedAmount ?? 0);
        }
        else if (this.pendingDataMessages.length < 64) this.pendingDataMessages.push(message);
      },
      sendEmote: (emote) => {
        this.sendEmote(emote);
      },
      onMessage: (listener) => {
        this.messageListeners.add(listener);
        for (const message of this.pendingMessages.splice(0)) listener(message);
        return () => this.messageListeners.delete(listener);
      },
      onDisconnect: (listener) => { this.disconnectListeners.add(listener); return () => this.disconnectListeners.delete(listener); },
      onReconnect: (listener) => { this.reconnectListeners.add(listener); return () => this.reconnectListeners.delete(listener); },
      onConnectionLost: (listener) => { this.connectionLostListeners.add(listener); return () => this.connectionLostListeners.delete(listener); },
      debugSnapshot: () => this.debugSnapshot(),
      onEmote: (listener) => { this.emoteListeners.add(listener); return () => this.emoteListeners.delete(listener); },
      close: () => this.close(),
    };
    this.readyResolve(api);
    this.resolved = true;
    this.readyResolve = null;
    this.readyReject = null;
  }

  private sendEmote(emote: NetworkEmote): void {
    if (this.emoteChannel?.readyState !== 'open') {
      this.pendingEmote = emote;
      return;
    }
    this.emoteChannel.send(JSON.stringify({ type: 'emote', protocolVersion: NETWORK_PROTOCOL_VERSION, networkMatchId: this.session.networkMatchId, seat: this.session.seat, sequence: this.emoteSequence++, emote }));
    this.messagesSent += 1;
  }

  private rejectReady(error: Error): void {
    this.clearConnectionTimeout();
    this.readyReject?.(error);
    this.readyResolve = null;
    this.readyReject = null;
  }

  private notifyDisconnected(): void {
    if (this.intentionallyClosed || !this.resolved || this.disconnected) return;
    this.disconnected = true;
    this.traceTransport('peer-connectivity', 'disconnected');
    for (const listener of this.disconnectListeners) listener();
    if (this.reconnectDeadlineTimer === null) {
      this.reconnectDeadlineTimer = globalThis.setTimeout(() => {
        this.reconnectDeadlineTimer = null;
        if (this.intentionallyClosed || !this.disconnected) return;
        for (const listener of this.connectionLostListeners) listener();
        this.close();
      }, this.reconnectDeadlineMs);
    }
  }

  private notifyConnected(): void {
    if (!this.resolved || !this.disconnected || this.channel?.readyState !== 'open') return;
    this.disconnected = false;
    this.traceTransport('peer-connectivity', 'connected');
    if (this.reconnectDeadlineTimer !== null) globalThis.clearTimeout(this.reconnectDeadlineTimer);
    this.reconnectDeadlineTimer = null;
    for (const listener of this.reconnectListeners) listener();
  }

  private peerFound(): void {
    this.onPeerFound?.();
    this.onPeerFound = null;
    if (this.resolved || this.connectionTimeoutTimer !== null) return;
    this.connectionTimeoutTimer = globalThis.setTimeout(() => {
      this.connectionTimeoutTimer = null;
      this.rejectReady(new Error('peer-connection-timeout'));
      this.close();
    }, this.connectionTimeoutMs);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutTimer === null) return;
    globalThis.clearTimeout(this.connectionTimeoutTimer);
    this.connectionTimeoutTimer = null;
  }

  private debugSnapshot(): NetworkTransportDebugSnapshot {
    return {
      schemaVersion: 1,
      linkStats: this.linkStats ?? { roundTripMs: null, inputDelayTicks: NETWORK_MAX_INPUT_DELAY_TICKS },
      route: this.selectedRoute,
      candidateTransport: this.candidateTransport,
      messagesSent: this.messagesSent,
      messagesReceived: this.messagesReceived,
      bufferedAmountHighWater: this.bufferedAmountHighWater,
      reconnectAttempts: this.reconnectAttempts,
      trace: [...this.transportTrace],
    };
  }

  private traceTransport(kind: string, state?: string, attempt?: number): void {
    const atMs = Math.round(performance.now());
    this.transportTrace.push({ atMs, kind, ...(state === undefined ? {} : { state }), ...(attempt === undefined ? {} : { attempt }) });
    const cutoff = atMs - 60_000;
    while (this.transportTrace.length > 1_000 || (this.transportTrace[0]?.atMs ?? atMs) < cutoff) this.transportTrace.shift();
  }

  private async captureSelectedRoute(): Promise<void> {
    const connection = this.connection;
    if (!connection) return;
    try {
      const reports = await connection.getStats();
      let selectedPair: RTCStats | null = null;
      reports.forEach((report) => {
        const candidate = report as RTCStats & { type: string; state?: string; nominated?: boolean };
        if (candidate.type === 'candidate-pair' && candidate.state === 'succeeded' && candidate.nominated) selectedPair = candidate;
      });
      if (!selectedPair) return;
      const pair = selectedPair as RTCStats & { localCandidateId?: string; remoteCandidateId?: string };
      const local = pair.localCandidateId ? reports.get(pair.localCandidateId) as (RTCStats & { candidateType?: string; protocol?: string }) | undefined : undefined;
      const remote = pair.remoteCandidateId ? reports.get(pair.remoteCandidateId) as (RTCStats & { candidateType?: string; protocol?: string }) | undefined : undefined;
      this.selectedRoute = local?.candidateType === 'relay' || remote?.candidateType === 'relay' ? 'relay' : 'direct';
      this.candidateTransport = local?.protocol === 'tcp' || remote?.protocol === 'tcp' ? 'tcp' : local?.protocol === 'udp' || remote?.protocol === 'udp' ? 'udp' : 'unknown';
      this.traceTransport('selected-route', `${this.selectedRoute}:${this.candidateTransport}`);
    } catch {
      this.traceTransport('selected-route', 'unavailable');
    }
  }

  close(): void {
    this.intentionallyClosed = true;
    this.traceTransport('peer-close', 'intentional');
    this.clearConnectionTimeout();
    this.onPeerFound = null;
    if (this.reconnectTimer !== null) globalThis.clearTimeout(this.reconnectTimer);
    if (this.reconnectDeadlineTimer !== null) globalThis.clearTimeout(this.reconnectDeadlineTimer);
    if (this.latencyProbeTimer !== null) globalThis.clearTimeout(this.latencyProbeTimer);
    this.reconnectTimer = null;
    this.reconnectDeadlineTimer = null;
    this.latencyProbeTimer = null;
    this.channel?.close();
    this.connection?.close();
    this.socket?.close();
    this.channel = null;
    this.connection = null;
    this.socket = null;
  }
}

export function websocketUrl(baseUrl: string, session: RoomSession): string {
  if (/^wss?:\/\//.test(session.websocketUrl)) return session.websocketUrl;
  const url = new URL(baseUrl, location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = `${url.pathname.replace(/\/$/, '')}/${session.websocketUrl.replace(/^\//, '')}`;
  url.searchParams.set('ticket', session.seatTicket);
  return url.toString();
}

export function seatIsPolite(seat: NetworkSeat): boolean { return seat === 'seat-b'; }
